import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { Sparkles, Users, Music, Heart, Headphones, Award, Crown } from "lucide-react";

export default function BaptemesCommunions() {
  useSeo({
    title: "DJ baptême & communion Nord | My Animation",
    description:
      "Animation musicale pour baptême, communion, repas de famille et événement privé dans le Nord, le Pas-de-Calais et les Hauts-de-France. Forfaits dès 450 €.",
    path: "/baptemes-communions",
    keywords: "DJ baptême Nord, DJ communion Lille, DJ repas de famille, animation musicale baptême Hauts-de-France",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Animation musicale baptême & communion",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animation musicale familiale pour baptême, communion, repas de famille et événement religieux ou familial.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Baptêmes & Communions"
        title={<>Des animations <span className="italic gold-text">familiales.</span></>}
        subtitle="Animation musicale familiale pour baptêmes, communions, repas de famille et événements religieux ou familiaux dans le Nord, le Pas-de-Calais et les Hauts-de-France."
        img="https://images.unsplash.com/photo-1438032005730-c779502df39b?auto=format&fit=crop&w=1600&q=80"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Une journée à vivre pleinement</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Une ambiance <br/><span className="italic gold-text">douce & festive.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation accompagne les familles pour les baptêmes, communions
              et grands repas familiaux : fond musical élégant pendant le repas,
              animation des temps forts (entrée, gâteau, jeux), puis bal en fin
              de soirée si souhaité. Une prestation adaptée à tous les âges et
              à la dimension familiale de votre événement.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Sonorisation discrète pendant le repas",
                "Animation des temps forts",
                "Playlist intergénérationnelle",
                "Micros pour discours et témoignages",
                "Mise en lumière en option",
                "Photobooth disponible (dès 179 €)",
                "Format demi-journée ou soirée complète",
                "Intervention dans tout le Nord & Pas-de-Calais",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <CatalogPackages
        category="bapteme"
        title="Nos forfaits DJ Baptême & Communion"
        subtitle={(n) => (<>{n || "Nos"} forfait{n > 1 ? "s" : ""}, <span className="italic gold-text">émotion garantie.</span></>)}
        icons={[Headphones, Award, Crown]}
        testidPrefix="bapt-pack-"
        featuredBadgeLabel="Le plus populaire"
        ctaLabel="Pré-réserver ce forfait"
        footerNote="Tarifs TTC · Hors options spécifiques · Devis personnalisé disponible"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Idées d'animation</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Pour <span className="italic gold-text">marquer la journée.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Heart, t: "Baptême", d: "Fond musical doux, animation famille, bal en fin de journée." },
              { i: Sparkles, t: "Communion", d: "Repas en famille, jeux musicaux, ambiance chaleureuse." },
              { i: Users, t: "Repas de famille", d: "Anniversaire de mariage, retrouvailles, fêtes religieuses." },
              { i: Music, t: "Photobooth & animations", d: "Souvenirs en photo, blind test famille, karaoké léger." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}
